import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Send, Trash2, Plane, ArrowUpRight, GripVertical } from 'lucide-react';
import { cn } from '../../lib/utils';
import axios from 'axios';
import { toast } from 'sonner';
import { LeadsStore } from '../../lib/leadsStore';
import Markdown from './Markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/wingman`;
const NOTES_API = `${BACKEND_URL}/api/notes`;

const SUGGESTIONS = [
  'Show me hot leads',
  'Open Megan Doyle',
  'Build the application from my notes',
  'Mark Derek as warm',
  'Create a new lead: Jane Doe, refi, 420k',
  'Schedule a callback for Tommy tomorrow at 10am',
  'Draft a reply for Aisha — pre-approval ready by 11am',
];

// ---------------------------------------------------------------------------
// Wingman directive parser  (UNCHANGED — full action protocol preserved)
// ---------------------------------------------------------------------------
function parseActions(rawReply) {
  if (!rawReply) return { text: '', actions: [] };
  const actions = [];
  const lines = rawReply.split('\n');
  const keep = [];
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) { keep.push(rawLine); continue; }
    const stripped = line.replace(/^[\*>`\-\s]+/, '');
    const arrow = /^(?:→|->|=>)\s*/;
    if (!arrow.test(stripped)) { keep.push(rawLine); continue; }
    const cmd = stripped.replace(arrow, '');

    let m = cmd.match(/^NAV\s+(\S+)\s*$/i);
    if (m) { actions.push({ type: 'navigate', to: m[1] }); continue; }

    m = cmd.match(/^FILTER\s+(\S+)\s*$/i);
    if (m) { actions.push({ type: 'filter', bucket: m[1].toLowerCase() }); continue; }

    m = cmd.match(/^DISP\s+(\S+)\s+(\S+)\s*$/i);
    if (m) { actions.push({ type: 'set_lead', id: m[1], patch: { temp: m[2].toLowerCase() } }); continue; }

    m = cmd.match(/^SET\s+lead:([^.\s]+)\.([A-Za-z_][\w]*)\s*=\s*(.+?)\s*$/i);
    if (m) { actions.push({ type: 'set_lead', id: m[1], patch: { [m[2]]: stripQuotes(m[3]) } }); continue; }

    m = cmd.match(/^SET\s+app\.([A-Za-z_][\w]*)\s*=\s*(.+?)\s*$/i);
    if (m) {
      const field = m[1];
      let value = stripQuotes(m[2]);
      if (['loanAmount','rate','loanTerm','propertyValue','purchasePrice','taxes','insurance','hoa','monthlyBaseIncome','monthlyOvertime','monthlyBonus','monthlyCommission','monthlyOther','monthlyDebts','creditScore','dependents','yearsAtAddress','monthsAtAddress','yearsEmployed','monthsEmployed'].includes(field)) {
        const n = Number(String(value).replace(/[$,%\s]/g, ''));
        if (!isNaN(n)) value = n;
      }
      actions.push({ type: 'fill_app', field, value });
      continue;
    }

    m = cmd.match(/^ADD_ASSET\s+(.+)$/i);
    if (m) {
      const kv = parseKv(m[1]);
      const asset = {
        type: kv.type ? stripQuotes(kv.type) : 'Checking',
        institution: kv.institution ? stripQuotes(kv.institution) : '',
        balance: kv.balance != null ? Number(String(kv.balance).replace(/[$,]/g, '')) || 0 : 0,
      };
      actions.push({ type: 'add_asset', asset });
      continue;
    }

    m = cmd.match(/^CREATE_LEAD\s+(.+)$/i);
    if (m) {
      const payload = parseKv(m[1]);
      ['estAmount', 'score'].forEach((k) => {
        if (payload[k] != null) {
          const n = Number(String(payload[k]).replace(/[$,]/g, ''));
          if (!isNaN(n)) payload[k] = n;
        }
      });
      if (payload.name) actions.push({ type: 'create_lead', payload });
      continue;
    }

    m = cmd.match(/^DELETE_LEAD\s+lead:(.+?)\s*$/i);
    if (m) { actions.push({ type: 'delete_lead', id: stripQuotes(m[1]) }); continue; }

    m = cmd.match(/^SCHEDULE\s+lead:(\S+)\s+(.+?)\s*$/i);
    if (m) {
      const id = m[1];
      const rest = m[2].trim();
      const dq = rest.match(/^"([^"]+)"\s+"([^"]+)"\s*$/);
      const sq = rest.match(/^'([^']+)'\s+'([^']+)'\s*$/);
      if (dq || sq) {
        const [, task, when] = dq || sq;
        actions.push({ type: 'schedule', id, task, when });
      } else {
        const piped = rest.split('|').map((s) => stripQuotes(s.trim()));
        if (piped.length === 2) actions.push({ type: 'schedule', id, task: piped[0], when: piped[1] });
        else actions.push({ type: 'schedule', id, task: stripQuotes(rest), when: '' });
      }
      continue;
    }

    m = cmd.match(/^DRAFT_REPLY\s+thread:(\S+)\s+(.+?)\s*$/i);
    if (m) { actions.push({ type: 'draft_reply', threadId: m[1], body: stripQuotes(m[2]) }); continue; }

    m = cmd.match(/^SEND_REPLY\s+thread:(\S+)\s+(.+?)\s*$/i);
    if (m) { actions.push({ type: 'send_reply', threadId: m[1], body: stripQuotes(m[2]) }); continue; }

    m = cmd.match(/^BUILD_APP\s+lead:(.+?)\s*$/i);
    if (m) { actions.push({ type: 'build_app', id: stripQuotes(m[1]) }); continue; }

    m = cmd.match(/^NOTE\s+lead:(\S+)\s+(.+)$/i);
    if (m) { actions.push({ type: 'add_note', leadId: m[1], body: stripQuotes(m[2]) }); continue; }

    m = cmd.match(/^NOTE\s+(.+)$/i);
    if (m) { actions.push({ type: 'add_note', leadId: null, body: stripQuotes(m[1]) }); continue; }

    keep.push(rawLine);
  }
  while (keep.length && keep[keep.length - 1].trim() === '') keep.pop();

  // Tenure → employmentStart: collapse any years/months Employed `fill_app`
  // directives into a single MM/DD/YYYY employmentStart computed from today.
  // This is the safety net so the wingman can say "→SET app.yearsEmployed=7"
  // and the form still gets the correct calendar date populated.
  let years = null, months = null, firstIdx = -1;
  const filtered = [];
  actions.forEach((a, i) => {
    if (a.type === 'fill_app' && a.field === 'yearsEmployed') {
      if (firstIdx === -1) firstIdx = filtered.length;
      years = Number(a.value) || 0;
    } else if (a.type === 'fill_app' && a.field === 'monthsEmployed') {
      if (firstIdx === -1) firstIdx = filtered.length;
      months = Number(a.value) || 0;
    } else {
      filtered.push(a);
    }
  });
  if (years != null || months != null) {
    const d = new Date();
    d.setMonth(d.getMonth() - ((years || 0) * 12 + (months || 0)));
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const employmentStart = `${mm}/${dd}/${d.getFullYear()}`;
    filtered.splice(firstIdx, 0, { type: 'fill_app', field: 'employmentStart', value: employmentStart });
  }

  return { text: keep.join('\n').trim(), actions: filtered };
}

function stripQuotes(s) {
  if (typeof s !== 'string') return s;
  return s.trim().replace(/^["'`]|["'`]$/g, '');
}

function parseKv(input) {
  const out = {};
  const re = /([A-Za-z_][\w]*)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s]+))/g;
  let m;
  while ((m = re.exec(input)) !== null) {
    const key = m[1];
    const val = m[2] != null ? m[2] : (m[3] != null ? m[3] : m[4]);
    out[key] = val;
  }
  return out;
}

function describeAction(a) {
  if (a.type === 'navigate') return `Opened ${a.to}`;
  if (a.type === 'filter') return `Filter: ${a.bucket}`;
  if (a.type === 'set_lead') {
    const lead = LeadsStore.get(a.id);
    const name = lead?.name || a.id;
    const fld = Object.keys(a.patch)[0];
    const val = a.patch[fld];
    return `${name}: ${fld} → ${typeof val === 'number' ? val.toLocaleString() : val}`;
  }
  if (a.type === 'fill_app') {
    const val = a.value;
    return `${a.field} → ${typeof val === 'number' ? val.toLocaleString() : val}`;
  }
  if (a.type === 'add_asset') {
    const b = a.asset?.balance;
    return `Asset: ${a.asset?.type || ''} @ ${a.asset?.institution || '—'}${b ? ` ($${Number(b).toLocaleString()})` : ''}`;
  }
  if (a.type === 'add_note') return `Note saved${a.leadId ? ' (lead)' : ' (team)'}`;
  if (a.type === 'create_lead') return `New lead: ${a.payload?.name || ''}`;
  if (a.type === 'delete_lead') return `Removed: ${a.id}`;
  if (a.type === 'schedule') return `Scheduled: ${a.task}${a.when ? ' · ' + a.when : ''}`;
  if (a.type === 'draft_reply') return 'Draft → composer';
  if (a.type === 'send_reply') return 'Reply sent';
  if (a.type === 'build_app') return 'Building 1003 from notes…';
  return a.type;
}

function persistedSession() {
  try { return localStorage.getItem('wingman_session') || null; } catch { return null; }
}
function setPersistedSession(s) { try { localStorage.setItem('wingman_session', s); } catch {} }
function clearPersistedSession() { try { localStorage.removeItem('wingman_session'); } catch {} }

// ---------------------------------------------------------------------------
// Corner / drag helpers
// ---------------------------------------------------------------------------
const VALID_CORNERS = ['bl', 'br', 'tl', 'tr'];
function loadCorner() {
  try {
    // One-time migration: existing users had 'bl' as the default, which
    // overlaps the new sidebar UserPanel. Force the move to 'br' once.
    const migrated = localStorage.getItem('wingman_corner_migrated_v2');
    if (!migrated) {
      const prev = localStorage.getItem('wingman_corner');
      if (prev === 'bl') localStorage.setItem('wingman_corner', 'br');
      localStorage.setItem('wingman_corner_migrated_v2', '1');
    }
    const c = localStorage.getItem('wingman_corner');
    return VALID_CORNERS.includes(c) ? c : 'br';
  } catch { return 'br'; }
}
function saveCorner(c) { try { localStorage.setItem('wingman_corner', c); } catch {} }

function fabPositionStyle(corner) {
  const v = corner[0] === 'b' ? { bottom: 24 } : { top: 24 };
  const h = corner[1] === 'r' ? { right: 24 } : { left: 24 };
  return { ...v, ...h };
}
function panelPositionStyle(corner) {
  const v = corner[0] === 'b' ? { bottom: 96 } : { top: 96 };
  const h = corner[1] === 'r' ? { right: 24 } : { left: 24 };
  return { ...v, ...h };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function EmergentWingman() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(persistedSession());
  const [msgs, setMsgs] = useState([
    { role: 'assistant', content: "I'm Wingman — your Kinfolk AI copilot. Ask me to draft messages, summarize leads, suggest next actions, or explain mortgage products. What's up?" },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Drag state (FAB position)
  const [corner, setCorner] = useState(loadCorner);
  const [dragging, setDragging] = useState(false);
  const [dragPos, setDragPos] = useState(null); // {x,y} only while dragging
  const dragMeta = useRef({ startX: 0, startY: 0, moved: false });

  // Composer height — user can drag the grab-bar above the composer upward to
  // grow the input area. Persisted per-user so power LOs keep their preferred
  // drafting height across sessions.
  const COMPOSER_MIN = 96;
  const COMPOSER_MAX = 520;
  const [composerH, setComposerH] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('wingman_composer_h') || '', 10);
      if (!isNaN(v)) return Math.max(COMPOSER_MIN, Math.min(COMPOSER_MAX, v));
    } catch {}
    return 120;
  });
  const composerDragRef = useRef({ active: false, startY: 0, startH: 0 });

  const onComposerHandleDown = (e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    composerDragRef.current = { active: true, startY: e.clientY, startH: composerH };
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {}
  };
  useEffect(() => {
    const onMove = (e) => {
      if (!composerDragRef.current.active) return;
      const dy = composerDragRef.current.startY - e.clientY; // dragging UP = positive
      const next = Math.max(COMPOSER_MIN, Math.min(COMPOSER_MAX, composerDragRef.current.startH + dy));
      setComposerH(next);
    };
    const onUp = () => {
      if (composerDragRef.current.active) {
        composerDragRef.current.active = false;
        try { localStorage.setItem('wingman_composer_h', String(composerH)); } catch {}
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [composerH]);

  const scrollRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // auto-scroll on new messages
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, loading]);

  // Hydrate prior session messages on first open
  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/history/${sessionId}`);
        if (!cancelled && r.data?.messages?.length) {
          setMsgs(r.data.messages.map((m) => {
            if (m.role === 'assistant') {
              const { text, actions } = parseActions(m.content);
              return { role: 'assistant', content: text || m.content, actions };
            }
            return { role: m.role, content: m.content };
          }));
        }
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ---- Drag handlers (pointer events) ----
  const onPointerDown = (e) => {
    // Left button only; ignore touch on the close/reset buttons inside the panel
    if (e.button !== 0) return;
    dragMeta.current = { startX: e.clientX, startY: e.clientY, moved: false };
    setDragging(true);
    setDragPos({ x: e.clientX, y: e.clientY });
    try { e.target.setPointerCapture?.(e.pointerId); } catch {}
  };

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const dx = e.clientX - dragMeta.current.startX;
      const dy = e.clientY - dragMeta.current.startY;
      if (!dragMeta.current.moved && Math.hypot(dx, dy) > 6) {
        dragMeta.current.moved = true;
      }
      setDragPos({ x: e.clientX, y: e.clientY });
    };
    const onUp = (e) => {
      setDragging(false);
      const moved = dragMeta.current.moved;
      if (!moved) {
        // Treat as click — toggle the panel
        setOpen((v) => !v);
      } else {
        // Snap to nearest corner based on release position
        const w = window.innerWidth, h = window.innerHeight;
        const isRight = e.clientX > w / 2;
        const isBottom = e.clientY > h / 2;
        const next = `${isBottom ? 'b' : 't'}${isRight ? 'r' : 'l'}`;
        setCorner(next);
        saveCorner(next);
      }
      setDragPos(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [dragging]);

  function buildContext() {
    const ctx = { page: location.pathname };
    const m = location.pathname.match(/\/leads\/([^/]+)/);
    if (m) {
      const lead = LeadsStore.get(m[1]);
      if (lead) {
        ctx.leadId = lead.id;
        ctx.leadName = lead.name;
        ctx.leadTemp = lead.temp;
        ctx.source = lead.source;
        ctx.estAmount = lead.estAmount;
      }
    }
    if (location.pathname.startsWith('/loans/')) ctx.loanNumber = location.pathname.split('/')[2];
    return ctx;
  }

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setInput('');
    setMsgs((prev) => [...prev, { role: 'user', content: t }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: t,
        context: buildContext(),
      });
      const newSession = r.data.session_id;
      if (newSession && newSession !== sessionId) {
        setSessionId(newSession);
        setPersistedSession(newSession);
      }
      const { text, actions } = parseActions(r.data.reply);
      const visible = text || (actions.length ? 'Done.' : '');
      setMsgs((prev) => [...prev, { role: 'assistant', content: visible, actions }]);

      if (actions.length) {
        setTimeout(async () => {
          const currentLeadId = (() => {
            const m = location.pathname.match(/\/leads\/([^/]+)/);
            return m ? m[1] : null;
          })();
          const auditByLead = {};
          const logAudit = (leadId, line) => {
            if (!leadId) return;
            (auditByLead[leadId] = auditByLead[leadId] || []).push(line);
          };

          // Track which lead's application form the SET app.* / ADD_ASSET
          // directives target. Initialised from URL; updated by NAV /leads/<id>
          // so a chat-from-anywhere flow ("Change Megan's property address …")
          // still persists to Mongo even without a mounted LeadProfile.
          let targetLeadId = currentLeadId;
          const appPatchByLead = {};   // { leadId: { field: value, ... } }
          const assetsByLead = {};     // { leadId: [asset, ...] }

          for (const a of actions) {
            try {
              if (a.type === 'navigate') {
                const m = a.to.match(/^\/leads\/([^/]+)/);
                const seg = m ? m[1] : null;
                const isAlias = seg && (!/^l[a-z0-9]+$/i.test(seg) || ['current', 'new', 'latest'].includes(seg.toLowerCase()));
                if (isAlias && targetLeadId && /^l[a-z0-9]+$/i.test(targetLeadId)) {
                  // Alias like /leads/current — route to whatever lead we're
                  // already targeting (typically the brand-new one from
                  // CREATE_LEAD) instead of breaking with "Lead not found".
                  navigate(`/leads/${targetLeadId}`);
                  await new Promise((res) => setTimeout(res, 420));
                } else {
                  navigate(a.to);
                  if (m && /^l[a-z0-9]+$/i.test(seg) && !['current', 'new', 'latest'].includes(seg.toLowerCase())) {
                    targetLeadId = seg;
                    // Give LeadProfile time to mount + register its
                    // wingman:fillApp / wingman:addAsset listeners before we
                    // dispatch events at it.
                    await new Promise((res) => setTimeout(res, 420));
                  }
                }
              } else if (a.type === 'filter') {
                LeadsStore.setPendingFilter(a.bucket);
                window.dispatchEvent(new CustomEvent('wingman:filter', { detail: { bucket: a.bucket } }));
              } else if (a.type === 'set_lead') {
                let id = a.id;
                if (!/^l[\w]+$/i.test(id)) id = LeadsStore.findIdByName(id) || id;
                const updated = await LeadsStore.update(id, a.patch);
                if (updated) {
                  toast.success(`${updated.name}: ${Object.keys(a.patch)[0]} updated`);
                  Object.entries(a.patch).forEach(([k, v]) => logAudit(id, `Updated ${k} → ${v}`));
                } else {
                  toast.error(`Could not find lead: ${a.id}`);
                }
              } else if (a.type === 'create_lead') {
                const created = await LeadsStore.create(a.payload);
                if (created) {
                  toast.success(`Lead created: ${created.name}`);
                  // Route any subsequent →SET app.* / →ADD_ASSET directives in
                  // this same turn to the brand-new lead so the application
                  // dropdown values (Loan Purpose, Loan Amount, etc.) land on
                  // the right doc instead of the previously-open page.
                  targetLeadId = created.id;
                  navigate(`/leads/${created.id}`);
                  // Wait for LeadProfile to mount + register its event
                  // listeners before subsequent directives fire.
                  await new Promise((res) => setTimeout(res, 450));
                } else { toast.error('Could not create lead'); }
              } else if (a.type === 'delete_lead') {
                let id = a.id;
                if (!/^l[\w]+$/i.test(id)) id = LeadsStore.findIdByName(id) || id;
                const lead = LeadsStore.get(id);
                const ok = await LeadsStore.remove(id);
                if (ok) toast.success(`Lead removed: ${lead?.name || id}`);
                else toast.error(`Could not delete: ${a.id}`);
              } else if (a.type === 'schedule') {
                let id = a.id;
                if (!/^l[\w]+$/i.test(id)) id = LeadsStore.findIdByName(id) || id;
                const updated = await LeadsStore.update(id, { nextAction: a.task, nextActionAt: a.when });
                if (updated) toast.success(`${updated.name}: scheduled — ${a.task} (${a.when})`);
                else toast.error(`Could not schedule on: ${a.id}`);
              } else if (a.type === 'draft_reply') {
                if (location.pathname !== '/messages') navigate('/messages');
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent('wingman:draftReply', { detail: { threadId: a.threadId, body: a.body } }));
                }, location.pathname !== '/messages' ? 350 : 0);
                toast.success('Draft loaded into composer');
              } else if (a.type === 'send_reply') {
                try {
                  await axios.post(`${BACKEND_URL}/api/threads/${a.threadId}/messages`, {
                    body: a.body, author: 'Cody Matthews', role: 'wingman',
                  });
                  toast.success('Reply sent');
                  if (location.pathname === '/messages') {
                    window.dispatchEvent(new CustomEvent('wingman:draftReply', { detail: { threadId: a.threadId, body: '' } }));
                  }
                } catch { toast.error('Could not send reply'); }
              } else if (a.type === 'build_app') {
                let id = a.id;
                if (id === 'current') {
                  const m = location.pathname.match(/\/leads\/([^/]+)/);
                  id = m ? m[1] : null;
                }
                if (!id) {
                  toast.error('Open a lead first, then ask Wingman to build the application.');
                } else {
                  if (!/^l[\w]+$/i.test(id)) id = LeadsStore.findIdByName(id) || id;
                  const target = `/leads/${id}`;
                  if (location.pathname !== target) navigate(target);
                  await new Promise((res) => setTimeout(res, 350));
                  try {
                    const r = await axios.post(`${BACKEND_URL}/api/wingman/build_application`, { lead_id: id });
                    const fields = r.data.fields || {};
                    const leadPatch = r.data.lead_patch || {};
                    let count = 0;
                    for (const [field, value] of Object.entries(fields)) {
                      window.dispatchEvent(new CustomEvent('wingman:fillApp', { detail: { field, value } }));
                      count += 1;
                    }
                    if (Object.keys(leadPatch).length) await LeadsStore.update(id, leadPatch);
                    toast.success(`Built ${count} fields from notes`, { description: r.data.summary || '', duration: 8000 });
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Could not build application from notes.');
                  }
                }
              } else if (a.type === 'fill_app') {
                // Dispatch the event so a mounted LeadProfile updates locally
                window.dispatchEvent(new CustomEvent('wingman:fillApp', { detail: { field: a.field, value: a.value } }));
                // Always batch the patch for direct Mongo persistence. This is
                // what makes "change Megan's address" work from the dashboard
                // even when Megan's profile isn't mounted.
                if (targetLeadId) {
                  appPatchByLead[targetLeadId] = { ...(appPatchByLead[targetLeadId] || {}), [a.field]: a.value };
                }
                toast.success(`Application: ${a.field} updated`);
                const v = typeof a.value === 'number' ? a.value.toLocaleString() : a.value;
                logAudit(targetLeadId, `1003 — ${a.field} → ${v}`);
              } else if (a.type === 'add_asset') {
                if (!targetLeadId) {
                  toast.error('Open a lead first, then ask Wingman to add an asset.');
                } else {
                  window.dispatchEvent(new CustomEvent('wingman:addAsset', { detail: { asset: a.asset } }));
                  (assetsByLead[targetLeadId] = assetsByLead[targetLeadId] || []).push(a.asset);
                  toast.success(`Asset added: ${a.asset?.type} @ ${a.asset?.institution}`);
                  const b = a.asset?.balance;
                  logAudit(targetLeadId, `Asset — ${a.asset?.type} @ ${a.asset?.institution}${b ? ` ($${Number(b).toLocaleString()})` : ''}`);
                }
              } else if (a.type === 'add_note') {
                await axios.post(NOTES_API, { leadId: a.leadId || null, body: a.body });
                toast.success(a.leadId ? 'Note saved on lead' : 'Note saved to scratchpad');
              }
            } catch (err) {
              console.error('Wingman action failed', a, err);
              toast.error(`Action failed: ${a.type}`);
            }
          }

          // Persist all batched application patches to MongoDB. We merge into
          // the existing application doc so we don't blow away other fields.
          const touchedLeads = new Set([
            ...Object.keys(appPatchByLead),
            ...Object.keys(assetsByLead),
          ]);
          for (const leadId of touchedLeads) {
            try {
              const lead = LeadsStore.get(leadId);
              if (!lead) continue;
              const currentApp = lead.application || {};
              const merged = { ...currentApp, ...(appPatchByLead[leadId] || {}) };
              if (assetsByLead[leadId]?.length) {
                merged.assets = [...(currentApp.assets || []), ...assetsByLead[leadId]];
              }
              await LeadsStore.update(leadId, { application: merged });
            } catch (e) {
              console.error('Wingman application persist failed', leadId, e);
            }
          }

          for (const [leadId, lines] of Object.entries(auditByLead)) {
            try {
              const body = `🤖 Wingman edits:\n` + lines.map((l) => `• ${l}`).join('\n');
              await axios.post(NOTES_API, { leadId, body });
              window.dispatchEvent(new CustomEvent('kf:notes-changed', { detail: { leadId } }));
            } catch (_) { /* non-fatal */ }
          }
        }, 350);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Network error';
      setMsgs((prev) => [...prev, { role: 'assistant', content: `\u26a0 ${detail}` }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    if (sessionId) {
      try { await axios.delete(`${API}/history/${sessionId}`); } catch {}
    }
    clearPersistedSession();
    setSessionId(null);
    setMsgs([{ role: 'assistant', content: 'Fresh session. Ask me anything — leads, deals, drafting, math.' }]);
  };

  // FAB style: while dragging, follow pointer; otherwise snap to current corner.
  const fabStyle = dragging && dragPos
    ? { position: 'fixed', top: dragPos.y - 22, left: dragPos.x - 22, transition: 'none', cursor: 'grabbing', touchAction: 'none' }
    : { position: 'fixed', ...fabPositionStyle(corner), cursor: 'grab', touchAction: 'none' };

  const panelStyle = { position: 'fixed', ...panelPositionStyle(corner), height: '720px', maxHeight: 'calc(100vh - 8rem)' };

  return (
    <>
      {/* While dragging, show subtle corner targets so the user knows where it'll snap */}
      {dragging && <CornerHints active={dragPos ? guessCorner(dragPos.x, dragPos.y) : corner} />}

      {/* Floating launcher button — draggable */}
      <button
        data-testid="wingman-trigger"
        onPointerDown={onPointerDown}
        onContextMenu={(e) => e.preventDefault()}
        title="Drag to move · Click to open"
        style={fabStyle}
        className={cn(
          'z-40 group select-none',
          'flex items-center gap-2.5 pl-2 pr-3.5 py-2 rounded-full',
          'bg-neutral-950 text-white border border-white/10',
          'shadow-[0_18px_50px_-18px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)_inset]',
          'transition-transform duration-200',
          !dragging && 'hover:scale-[1.03] hover:shadow-[0_20px_60px_-18px_rgba(0,0,0,0.65)]'
        )}
      >
        <span className="relative h-8 w-8 rounded-full bg-gradient-to-br from-[hsl(var(--kf-gold))] to-[hsl(var(--kf-gold-deep))] text-neutral-900 flex items-center justify-center overflow-hidden ring-1 ring-[hsl(var(--kf-gold))]/40">
          <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.6),transparent_50%)]" />
          <Plane className="relative h-4 w-4 -rotate-12" strokeWidth={2.5} />
          {/* gentle pulse ring when idle */}
          {!open && !dragging && (
            <span className="absolute inset-0 rounded-full pulse-gold" />
          )}
        </span>
        <span className="flex flex-col items-start leading-none">
          <span className="text-[12.5px] font-semibold tracking-tight">Wingman</span>
        </span>
        <GripVertical className="h-3 w-3 text-white/25 group-hover:text-white/55 transition-colors" />
      </button>

      {/* Slide-out panel */}
      {open && (
        <div
          style={panelStyle}
          className={cn(
            'z-40 w-[560px] max-w-[calc(100vw-3rem)] flex flex-col overflow-hidden',
            'rounded-[24px] bg-neutral-950 text-white',
            'border border-white/10',
            'shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.05)_inset]',
            'animate-fadeUp'
          )}
        >
          {/* Header — premium dark with gold sweep */}
          <div className="relative px-5 py-4 shrink-0 border-b border-white/10 bg-gradient-to-b from-neutral-900 to-neutral-950 overflow-hidden">
            <div className="absolute inset-0 bg-dot-dark opacity-30 pointer-events-none" />
            <div className="absolute -right-12 -top-12 h-32 w-32 rounded-full bg-[hsl(var(--kf-gold))]/20 blur-3xl pointer-events-none" />
            <div className="absolute left-0 right-0 top-0 h-px gold-sweep" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative h-10 w-10 rounded-full bg-gradient-to-br from-[hsl(var(--kf-gold))] to-[hsl(var(--kf-gold-deep))] text-neutral-900 flex items-center justify-center overflow-hidden ring-1 ring-[hsl(var(--kf-gold))]/40">
                  <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.55),transparent_55%)]" />
                  <Sparkles className="relative h-4 w-4" strokeWidth={2.5} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[14px] font-bold tracking-tight">Wingman</span>
                    <span className="inline-flex items-center gap-1 text-[9px] font-bold tracking-[0.16em] bg-[hsl(var(--kf-gold))]/15 text-[hsl(var(--kf-gold))] border border-[hsl(var(--kf-gold))]/30 rounded px-1.5 py-0.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--kf-gold))] animate-pulse" />
                      ONLINE
                    </span>
                  </div>
                  <div className="text-[10.5px] text-white/50 mt-0.5 mono tracking-wide">Your Kinfolk AI copilot</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="wingman-reset"
                  onClick={reset}
                  title="New session"
                  className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid="wingman-close"
                  onClick={() => setOpen(false)}
                  className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages area */}
          <div
            ref={scrollRef}
            className="wingman-scroll flex-1 overflow-y-auto px-5 py-5 space-y-4 bg-neutral-50 text-neutral-900 relative"
          >
            <div className="absolute inset-0 bg-dot opacity-50 pointer-events-none" />
            <div className="relative space-y-4">
              {msgs.map((m, i) => (
                <Bubble key={i} m={m} />
              ))}
              {loading && <TypingIndicator />}
            </div>
          </div>

          {/* Suggestions — only show on first turn */}
          {msgs.length <= 1 && (
            <div className="px-4 pt-3 pb-2 flex gap-1.5 flex-wrap border-t border-neutral-200 shrink-0 bg-white">
              <span className="w-full text-[9.5px] uppercase tracking-[0.2em] font-bold text-neutral-400 mb-1">Try saying</span>
              {SUGGESTIONS.map((s) => (
                <button
                  data-testid={`wingman-suggestion-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  key={s}
                  onClick={() => send(s)}
                  disabled={loading}
                  className={cn(
                    'text-[11px] rounded-full bg-white px-3 py-1.5 text-neutral-700 font-medium',
                    'border border-neutral-200 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white',
                    'transition-all hover:-translate-y-0.5 hover:shadow-md',
                    'disabled:opacity-50 disabled:hover:translate-y-0'
                  )}
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="bg-white border-t border-neutral-200 shrink-0 flex flex-col"
            style={{ height: composerH }}
          >
            {/* Drag-up handle — sits at the very top of the composer.
                Drag UP to grow the input area, DOWN to shrink. */}
            <div
              data-testid="wingman-composer-resizer"
              onPointerDown={onComposerHandleDown}
              title="Drag to resize the composer"
              className="group relative h-3 cursor-ns-resize flex items-center justify-center select-none -mt-px"
            >
              <span className="h-1 w-10 rounded-full bg-neutral-200 group-hover:bg-[hsl(var(--kf-gold))] group-active:bg-[hsl(var(--kf-gold-deep))] transition-colors" />
            </div>

            <div className="px-3 pb-3 pt-0 flex-1 min-h-0">
              <div className="relative flex items-end gap-2 rounded-2xl border border-neutral-200 bg-neutral-50 px-3 py-2 focus-within:border-neutral-900 focus-within:bg-white focus-within:shadow-[0_0_0_4px_hsl(var(--kf-gold)/0.15)] transition-all h-full">
                <textarea
                  data-testid="wingman-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    // Cmd/Ctrl + Enter sends. Plain Enter inserts a newline so
                    // users can write multi-paragraph drafts comfortably.
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Ask Wingman anything…  (Enter for new line · ⌘/Ctrl + Enter to send)"
                  disabled={loading}
                  className="wingman-textarea flex-1 resize-none bg-transparent text-[13.5px] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none disabled:opacity-50 h-full py-1.5"
                />
                <button
                  type="submit"
                  data-testid="wingman-send"
                  disabled={loading || !input.trim()}
                  className={cn(
                    'h-9 w-9 rounded-xl flex items-center justify-center shrink-0 transition-all',
                    input.trim() && !loading
                      ? 'bg-gradient-to-br from-[hsl(var(--kf-gold))] to-[hsl(var(--kf-gold-deep))] text-neutral-900 shadow-[0_4px_14px_-4px_hsl(var(--kf-gold)/0.6)] hover:scale-105'
                      : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                  )}
                  title="Send (⌘/Ctrl + Enter)"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={2.5} />
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Local styles for the chat scrollbar (scoped via class) */}
      <style>{`
        .wingman-scroll::-webkit-scrollbar { width: 6px; }
        .wingman-scroll::-webkit-scrollbar-thumb { background: hsl(0 0% 80%); border-radius: 999px; }
        .wingman-scroll::-webkit-scrollbar-thumb:hover { background: hsl(0 0% 65%); }
        .wingman-scroll::-webkit-scrollbar-track { background: transparent; }
        /* Make the textarea resize grip more visible + gold-tinted on hover */
        .wingman-textarea { background-clip: padding-box; }
        .wingman-textarea::-webkit-resizer {
          background-color: transparent;
          background-image:
            linear-gradient(135deg, transparent 50%, hsl(0 0% 70%) 50%, hsl(0 0% 70%) 60%, transparent 60%, transparent 70%, hsl(0 0% 70%) 70%, hsl(0 0% 70%) 80%, transparent 80%);
          background-size: 12px 12px;
        }
        .wingman-textarea:hover::-webkit-resizer,
        .wingman-textarea:focus::-webkit-resizer {
          background-image:
            linear-gradient(135deg, transparent 50%, hsl(var(--kf-gold-deep)) 50%, hsl(var(--kf-gold-deep)) 60%, transparent 60%, transparent 70%, hsl(var(--kf-gold-deep)) 70%, hsl(var(--kf-gold-deep)) 80%, transparent 80%);
        }
        @keyframes wmDotPulse {
          0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
          40% { opacity: 1; transform: translateY(-2px); }
        }
        .wm-dot { animation: wmDotPulse 1.2s ease-in-out infinite; }
        @keyframes wmBubbleIn {
          from { opacity: 0; transform: translateY(6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .wm-bubble-in { animation: wmBubbleIn 0.32s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </>
  );
}

// ---------------------------------------------------------------------------
// Snap-target hints shown while dragging
// ---------------------------------------------------------------------------
function guessCorner(x, y) {
  const w = window.innerWidth, h = window.innerHeight;
  return `${y > h / 2 ? 'b' : 't'}${x > w / 2 ? 'r' : 'l'}`;
}
function CornerHints({ active }) {
  const corners = [
    { id: 'tl', cls: 'top-6 left-6' },
    { id: 'tr', cls: 'top-6 right-6' },
    { id: 'bl', cls: 'bottom-6 left-6' },
    { id: 'br', cls: 'bottom-6 right-6' },
  ];
  return (
    <>
      {corners.map((c) => (
        <div
          key={c.id}
          className={cn(
            'pointer-events-none fixed z-30 h-12 w-12 rounded-full border-2 border-dashed transition-all duration-150',
            c.cls,
            active === c.id
              ? 'border-[hsl(var(--kf-gold))] bg-[hsl(var(--kf-gold))]/15 scale-110'
              : 'border-neutral-300 bg-white/60'
          )}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bubble + supporting components
// ---------------------------------------------------------------------------
function Bubble({ m }) {
  const isUser = m.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end wm-bubble-in">
        <div
          className={cn(
            'max-w-[82%] px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap',
            'rounded-[18px] rounded-br-[6px]',
            'bg-neutral-900 text-white',
            'shadow-[0_2px_8px_-2px_rgba(0,0,0,0.25)]'
          )}
        >
          {m.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex justify-start gap-2 wm-bubble-in">
      <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-[hsl(var(--kf-gold))] to-[hsl(var(--kf-gold-deep))] text-neutral-900 flex items-center justify-center mt-0.5 shadow-sm ring-1 ring-[hsl(var(--kf-gold))]/30">
        <Sparkles className="h-3 w-3" strokeWidth={2.5} />
      </div>
      <div className="max-w-[82%] flex flex-col gap-1.5">
        {(m.content || '').trim() && (
          <div
            className={cn(
              'px-4 py-2.5 text-[13px] leading-relaxed text-neutral-900',
              'rounded-[18px] rounded-bl-[6px]',
              'bg-white border border-neutral-200',
              'shadow-[0_2px_8px_-3px_rgba(0,0,0,0.08)]'
            )}
          >
            <Markdown text={m.content} />
          </div>
        )}
        {Array.isArray(m.actions) && m.actions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pl-1">
            {m.actions.map((a, i) => (
              <span
                key={i}
                className={cn(
                  'inline-flex items-center gap-1 text-[10px] font-bold tracking-wide rounded-full px-2 py-1',
                  'bg-gradient-to-r from-[hsl(var(--kf-gold-soft))] to-white',
                  'text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/40',
                  'shadow-[0_1px_2px_rgba(0,0,0,0.04)]'
                )}
              >
                <ArrowUpRight className="h-2.5 w-2.5" />
                {describeAction(a)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="flex justify-start gap-2 wm-bubble-in">
      <div className="h-7 w-7 shrink-0 rounded-full bg-gradient-to-br from-[hsl(var(--kf-gold))] to-[hsl(var(--kf-gold-deep))] text-neutral-900 flex items-center justify-center mt-0.5 shadow-sm ring-1 ring-[hsl(var(--kf-gold))]/30">
        <Sparkles className="h-3 w-3" strokeWidth={2.5} />
      </div>
      <div className="px-4 py-3 rounded-[18px] rounded-bl-[6px] bg-white border border-neutral-200 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-1">
          <span className="wm-dot h-1.5 w-1.5 rounded-full bg-neutral-400" style={{ animationDelay: '0s' }} />
          <span className="wm-dot h-1.5 w-1.5 rounded-full bg-neutral-400" style={{ animationDelay: '0.18s' }} />
          <span className="wm-dot h-1.5 w-1.5 rounded-full bg-neutral-400" style={{ animationDelay: '0.36s' }} />
        </div>
      </div>
    </div>
  );
}
